import { useEffect, useState } from "react";

import { AlertTriangle } from "lucide-react";

import type { ContentBlock, LegalDocument, ListItem } from "./types";

const isDefinitionItem = (item: ListItem): item is { term: string; text: string } =>
  typeof item !== "string";

const Block = ({ block }: { block: ContentBlock }) => {
  switch (block.kind) {
    case "subheading":
      return (
        <h3 className="font-headline text-lg font-bold text-on-surface mt-8 mb-3">{block.text}</h3>
      );

    case "paragraph":
      return (
        <p
          className={`leading-relaxed mb-4 ${
            block.emphasis ? "text-on-surface font-semibold" : "text-on-surface-variant"
          }`}
        >
          {block.lead && <span className="font-bold text-on-surface">{block.lead} </span>}
          {block.text}
        </p>
      );

    case "list":
      return (
        <ul className="space-y-3 mb-6 pl-1">
          {block.items.map((item, index) => (
            <li key={index} className="flex gap-3 text-on-surface-variant leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary/60 shrink-0" />
              <span>
                {isDefinitionItem(item) ? (
                  <>
                    <span className="font-semibold text-on-surface">{item.term}</span> {item.text}
                  </>
                ) : (
                  item
                )}
              </span>
            </li>
          ))}
        </ul>
      );

    case "notice":
      return (
        <div className="flex gap-4 rounded-2xl bg-surface-container-high ring-1 ring-outline-variant/20 p-6 mb-8">
          <AlertTriangle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-on-surface leading-relaxed font-medium">{block.text}</p>
        </div>
      );

    case "address":
      return (
        <address className="not-italic text-on-surface-variant leading-relaxed mb-6">
          {block.lines.map((line, index) => (
            <span key={index} className="block">
              {line}
            </span>
          ))}
        </address>
      );

    default:
      return null;
  }
};

const LegalPage = ({ document: doc }: { document: LegalDocument }) => {
  const [activeId, setActiveId] = useState(doc.sections[0]?.id ?? "");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc.title]);

  // Highlight the section currently nearest the top of the viewport.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 },
    );

    doc.sections.forEach((section) => {
      const element = window.document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [doc.sections]);

  return (
    <div className="bg-background">
      <header className="border-b border-surface-container-high bg-surface-container-low">
        <div className="max-w-8xl mx-auto px-8 py-16">
          <h1 className="font-headline text-4xl md:text-5xl font-extrabold text-on-surface tracking-tight">
            {doc.title}
          </h1>
          <p className="text-on-surface-variant mt-4 text-lg">{doc.subtitle}</p>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mt-6 text-sm text-on-surface-variant/80">
            <span>
              Effective Date:{" "}
              <span className="text-on-surface font-medium">{doc.effectiveDate}</span>
            </span>
            <span>
              Last Updated: <span className="text-on-surface font-medium">{doc.lastUpdated}</span>
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-8xl mx-auto px-8 py-16 flex flex-col lg:flex-row gap-12">
        <nav
          aria-label="Sections"
          className="lg:w-72 shrink-0 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto"
        >
          <h2 className="text-xs font-bold text-on-surface-variant uppercase tracking-wider mb-4">
            On this page
          </h2>
          <ul className="space-y-1">
            {doc.sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
                    activeId === section.id
                      ? "bg-surface-container-high text-primary font-medium"
                      : "text-on-surface-variant hover:text-primary"
                  }`}
                >
                  {section.number}. {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <article className="min-w-0 flex-1 max-w-3xl">
          {doc.preamble?.map((block, index) => (
            <Block key={index} block={block} />
          ))}

          {doc.sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-24 mb-14">
              <h2 className="font-headline text-2xl font-bold text-on-surface mb-5">
                <span className="text-primary">{section.number}.</span> {section.title}
              </h2>
              {section.blocks.map((block, index) => (
                <Block key={index} block={block} />
              ))}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
};

export default LegalPage;
